import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import jwtDecode from 'jwt-decode';

import { sendToAnalyticsCustomEventObj } from 'lib/googleAnalytics';
import {
  AuthResponse,
  RegistrationTokenData,
  ResponseRegistrationToken,
  TokenPayload,
} from './models/response/AuthResponse';

const ACCESS_TOKEN_LOCAL_STORAGE_KEY = 'accessToken';
const REFRESH_TOKEN_LOCAL_STORAGE_KEY = 'refreshToken';
const REGISTRATION_TOKEN_LOCAL_STORAGE_KEY = 'registrationToken';
const CC_WSS_URL_LOCAL_STORAGE_KEY = 'ccWsServerUrl';
const OAUTH_SERVER_URL_LOCAL_STORAGE_KEY = 'oauthServerUrl';
const CONNECTION_KEY_LOCAL_STORAGE_KEY = 'connectionKey';

class OauthApiArgs {
  method: string = 'POST';
  baseUrl: string = localStorage.getItem(OAUTH_SERVER_URL_LOCAL_STORAGE_KEY);
  path: string = '';
  params: string = '';
  options = {};
  headers?: Headers;
  data: unknown;

  get fullUrl(): string {
    return this.baseUrl + this.path + (this.params ? '?' + new URLSearchParams(this.params) : '');
  }
}

const oauthApi = async <T>(args: OauthApiArgs): Promise<T | null> => {
  // If the OAuth server URL is not filled in, we ignore the token receiving functionality.
  if (!args.baseUrl) {
    return;
  }

  const rawResponse = await fetch(args.fullUrl, {
    method: args.method,
    headers: args.headers,
    body: args.data ? JSON.stringify(args.data) : null,
    ...args.options,
  });

  if (!rawResponse.ok) {
    const toest_error = `status: ${rawResponse.status}. statusText:${
      rawResponse.statusText
    }. text:${await rawResponse.text()}.`;
    if (rawResponse.status !== 401 && rawResponse.status !== 404) {
      toast.error(rawResponse.status + ' ' + rawResponse.statusText);
    }
    sendToAnalyticsCustomEventObj('oauth_api_error', {
      status: rawResponse.status,
      message: rawResponse.statusText,
    });
    throw new Error(toest_error);
  } else if (rawResponse.status === 204) {
    // No Content or not JSON type
    return null;
  } else {
    // Check if the response has content
    const contentType = rawResponse.headers.get('content-type');
    if (contentType) {
      return await rawResponse.json();
    }
  }
  return null;
};

const fetchCognitoIdToken = async (): Promise<string | null> => {
  try {
    // Attempt to get the current user session token
    return (await Auth.currentSession()).getIdToken().getJwtToken();
  } catch (error) {
    console.error('Error retrieving user session: ', error);
    return null;
  }
};

/**
 * Reusable wrapper for oauthApi that injects Cognito ID token into Authorization header
 * and ensures JSON content type when a body is present.
 */
export const oauthApiWithIdToken = async <T>(args: OauthApiArgs): Promise<T> => {
  const cognitoIdToken = await fetchCognitoIdToken();
  if (!cognitoIdToken) {
    throw new Error('No Cognito ID token available');
  }

  const headers = new Headers(args.headers || {});
  headers.set('Authorization', `Bearer ${cognitoIdToken}`);
  if (args.data && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const mergedArgs = new OauthApiArgs();
  mergedArgs.path = args.path;
  mergedArgs.method = args.method;
  mergedArgs.headers = headers;
  mergedArgs.data = args.data;

  return oauthApi<T>(mergedArgs);
};

const fetchAccessToken = async (
  baseUrl: string | null = null,
  connectionKey?: string,
): Promise<[string | null, string | null]> => {
  const cognitoIdToken = await fetchCognitoIdToken();
  if (!cognitoIdToken) {
    return [null, null];
  }

  const headers = new Headers();
  headers.set('Authorization', `Bearer ${cognitoIdToken}`);
  headers.set('Content-Type', 'application/json');

  const args = new OauthApiArgs();
  args.path = '/api/token/access';
  args.headers = headers;
  if (baseUrl) {
    args.baseUrl = baseUrl;
  }
  args.data = {
    device_connection_key: connectionKey || null,
  };

  const response = await oauthApi<AuthResponse>(args);
  if (response && 'access_token' in response) {
    return [response.access_token, response.refresh_token];
  }
  return [null, null];
};

const fetchRegistrationToken = async (
  baseUrl: string | null = null,
  connectionKey?: string,
  forceGettingNewToken?: boolean,
): Promise<{ registrationToken: string; accessToken: string } | null> => {
  const accessToken = await getAccessToken(baseUrl, connectionKey, forceGettingNewToken);
  if (!accessToken) {
    return null;
  }
  const headers = new Headers();
  headers.set('Authorization', `Bearer ${accessToken}`);

  const args = new OauthApiArgs();
  args.path = '/api/token/registration';
  args.headers = headers;
  if (baseUrl) {
    args.baseUrl = baseUrl;
  }
  const response = await oauthApi<ResponseRegistrationToken>(args);
  if (response && 'registration_token' in response) {
    return { registrationToken: response.registration_token, accessToken };
  }
  return null;
};

const getTokenPayload = <T>(token: string): T | null => {
  if (!token) {
    return;
  }
  return jwtDecode<T>(token);
};

const isTokenValid = (token: string | null | undefined): boolean => {
  if (!token) {
    return false;
  }

  const decodedToken = getTokenPayload<TokenPayload>(token);
  if (decodedToken && decodedToken.exp) {
    const halfwayExpiration = new Date(
      decodedToken.exp * 1000 - (decodedToken.exp * 1000 - decodedToken.iat * 1000) / 2,
    );
    const currentTime = new Date(new Date().toISOString());

    if (currentTime > halfwayExpiration) {
      return false;
    }
  }
  return true;
};

const extractDeviceConnectionKeyFromRegistrationToken = (token: string): string | null => {
  const payload = getTokenPayload<TokenPayload>(token);
  if (payload) {
    return payload.device_connection_key;
  }
  return null;
};

const getRegistrationTokenData = (token: string): RegistrationTokenData | null => {
  const payload = getTokenPayload<TokenPayload<RegistrationTokenData>>(token);
  if (payload) {
    return payload.data;
  }
  return null;
};

const getWebSocketServerUrl = (token: string): string | null => {
  const tokenData = getRegistrationTokenData(token);
  if (tokenData) {
    return tokenData.wss_cc_server_url;
  }
  return null;
};

const removeTokens = (serverUrl: string): void => {
  if (!serverUrl) {
    return;
  }

  const oauthServerUrl = localStorage.getItem(OAUTH_SERVER_URL_LOCAL_STORAGE_KEY);
  if (oauthServerUrl !== serverUrl) {
    localStorage.setItem(OAUTH_SERVER_URL_LOCAL_STORAGE_KEY, serverUrl);
    window.dispatchEvent(new CustomEvent('oauthServerUrlReady', { detail: serverUrl }));
    localStorage.removeItem(REGISTRATION_TOKEN_LOCAL_STORAGE_KEY);
    localStorage.removeItem(CC_WSS_URL_LOCAL_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY);
    localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
  }
};

const getAccessToken = async (
  serverUrl: string | null = null,
  connectionKey?: string,
  forceGettingNewToken?: boolean,
): Promise<string | null> => {
  removeTokens(serverUrl);

  let accessToken: string = localStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
  let refreshToken: string;
  if (!isTokenValid(accessToken) || forceGettingNewToken) {
    [accessToken, refreshToken] = await fetchAccessToken(serverUrl, connectionKey);
    if (!accessToken) {
      localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY);
      return;
    }
    localStorage.setItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY, refreshToken);
  }
  return accessToken;
};

export const getAccessTokenForWiFiConnection = async (
  deviceConnectionKey?: string,
): Promise<{ access_token: string; refresh_token: string } | null> => {
  const args = new OauthApiArgs();
  args.path = '/api/token/access';
  args.data = {
    device_connection_key: deviceConnectionKey || null,
  };

  // Reuse oauthApi with Cognito ID token for Authorization
  const response = await oauthApiWithIdToken<{ access_token: string; refresh_token: string }>(args);

  if (response && response.access_token && response.refresh_token) {
    localStorage.setItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY, response.access_token);
    localStorage.setItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY, response.refresh_token);
    return response;
  } else {
    localStorage.removeItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_LOCAL_STORAGE_KEY);
    return null;
  }
};

const getRegistrationToken = async (
  serverUrl: string | null = null,
  connectionKey?: string,
  forceGettingNewToken?: boolean,
): Promise<[string | null, string | null, string | null]> => {
  removeTokens(serverUrl);
  let registrationToken: string = localStorage.getItem(REGISTRATION_TOKEN_LOCAL_STORAGE_KEY);
  let webSocketServerUrl: string = localStorage.getItem(CC_WSS_URL_LOCAL_STORAGE_KEY);
  let accessToken: string = localStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);

  if (!isTokenValid(registrationToken) || forceGettingNewToken) {
    const { registrationToken: registrationTokenFromServer, accessToken: accessTokenFromServer } =
      await fetchRegistrationToken(serverUrl, connectionKey, forceGettingNewToken);
    registrationToken = registrationTokenFromServer;
    accessToken = accessTokenFromServer;
    if (!registrationToken) {
      localStorage.removeItem(REGISTRATION_TOKEN_LOCAL_STORAGE_KEY);
      localStorage.removeItem(CC_WSS_URL_LOCAL_STORAGE_KEY);
      return [null, null, null];
    }
    webSocketServerUrl = getWebSocketServerUrl(registrationToken);
    localStorage.setItem(REGISTRATION_TOKEN_LOCAL_STORAGE_KEY, registrationToken);
    localStorage.setItem(CC_WSS_URL_LOCAL_STORAGE_KEY, webSocketServerUrl);
  }
  return [registrationToken, webSocketServerUrl, accessToken];
};

const getTokenValidationTime = async (baseURL: string, jwtToken: string): Promise<number> => {
  const headers = new Headers();
  headers.set('Authorization', `Bearer ${jwtToken}`);

  const args = new OauthApiArgs();
  args.baseUrl = baseURL;
  args.path = '/api/token/validation';
  args.headers = headers;

  const start = performance.now();
  try {
    await oauthApi(args);
    return performance.now() - start;
  } catch {
    return -1;
  }
};

const getActualCcWsServerUrl = (): string | null => {
  return localStorage.getItem(CC_WSS_URL_LOCAL_STORAGE_KEY);
};

const getActualOauthServerUrl = (): string | null => {
  return localStorage.getItem(OAUTH_SERVER_URL_LOCAL_STORAGE_KEY);
};

const getWifiConnectionKeyOnLocalStorage = (): string | null => {
  return localStorage.getItem(CONNECTION_KEY_LOCAL_STORAGE_KEY);
};

const setWiFiConnectionKeyOnLocalStorage = (connectionKey?: string | null): void => {
  localStorage.setItem(CONNECTION_KEY_LOCAL_STORAGE_KEY, connectionKey || '');
};
const clearWiFiConnectionKeyOnLocalStorage = (): void => {
  localStorage.removeItem(CONNECTION_KEY_LOCAL_STORAGE_KEY);
};

// Aliases to match existing imports/usages across the app
const getDeviceConnectionKey = (token: string): string | null => {
  return extractDeviceConnectionKeyFromRegistrationToken(token);
};

export {
  fetchAccessToken,
  getRegistrationToken,
  getAccessToken,
  getTokenValidationTime,
  getActualCcWsServerUrl,
  getActualOauthServerUrl,
  extractDeviceConnectionKeyFromRegistrationToken,
  getDeviceConnectionKey,
  getWifiConnectionKeyOnLocalStorage,
  setWiFiConnectionKeyOnLocalStorage,
  clearWiFiConnectionKeyOnLocalStorage,
};
